import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAddress } from "@thirdweb-dev/react";
import { ethers } from "ethers";
import "../styles/staking-box4.css";
import { useStakingContract } from "../hooks/useStakingContract";

const StakingBox4 = () => {
  const { t } = useTranslation();
  const address = useAddress();
  const [rewards, setRewards] = useState("0");
  const [displayRewards, setDisplayRewards] = useState("0.0000");
  const [isClaiming, setIsClaiming] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [blockFetch, setBlockFetch] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState("success"); // success, error
  
  // Referencias para mantener valores entre renderizados
  const intervalRef = useRef(null);
  const timerRef = useRef(null);
  const lastSyncTimeRef = useRef(Date.now());
  const baseRewardRef = useRef("0");
  const stakeAmountRef = useRef(null);
  
  // Obtener contrato y función de cálculo de recompensas
  const { contract, calculateRewards } = useStakingContract();
  
  // Obtener recompensas desde el contrato (solo cada 5 segundos)
  const fetchRewards = async () => {
    if (!address || !contract || blockFetch) return;
    
    try {
      setIsRefreshing(true);
      
      // Obtener recompensas actuales del contrato
      const currentRewards = await calculateRewards();
      setRewards(currentRewards);
      
      // Guardar para cálculos locales
      baseRewardRef.current = currentRewards;
      lastSyncTimeRef.current = Date.now();
      
      // Obtener stakeAmount para cálculos si no lo tenemos
      if (!stakeAmountRef.current) {
        const stakeAmount = await contract.balanceOf(address);
        stakeAmountRef.current = stakeAmount;
      }
      
      // Formatear para mostrar
      setDisplayRewards(formatEther(currentRewards));
      
    } catch (error) {
      console.error("Error al obtener recompensas:", error);
    } finally {
      setIsRefreshing(false);
    }
  };
  
  // Función para formatear ETH con 4 decimales
  const formatEther = (value) => {
    if (!value) return "0.0000";
    try {
      return parseFloat(ethers.utils.formatEther(value)).toFixed(4);
    } catch (error) {
      console.error("Error de formateo:", error);
      return "0.0000";
    }
  };
  
  // Actualizar contador visualmente cada 200ms (simulación visual más lenta)
  const startVisualCounter = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    
    timerRef.current = setInterval(() => {
      if (blockFetch || !stakeAmountRef.current) return;
      
      try {
        // Calcular segundos desde última sincronización
        const now = Date.now();
        const secondsElapsed = (now - lastSyncTimeRef.current) / 1000;
        
        // Convertir stake y base a ETH para cálculos
        const stakeEth = parseFloat(ethers.utils.formatEther(stakeAmountRef.current));
        const baseRewardEth = parseFloat(ethers.utils.formatEther(baseRewardRef.current));
        
        // Calcular incremento (108% anual ÷ segundos en año)
        const rewardPerSecond = (stakeEth * 108) / (100 * 31536000);
        const increment = rewardPerSecond * secondsElapsed;
        
        // Calcular nuevo valor y formatear
        const newRewardEth = baseRewardEth + increment;
        
        // Actualizar display
        setDisplayRewards(newRewardEth.toFixed(4));
        
        // Convertir a BigNumber para mantener en estado (para comparaciones)
        const newRewardWei = ethers.utils.parseEther(newRewardEth.toString());
        setRewards(newRewardWei.toString());
        
      } catch (error) {
        console.error("Error en contador visual:", error);
      }
    }, 100000); // Cambiado de 50ms a 200ms para una actualización más suave
  };
  
  // Efecto para iniciar/sincronizar
  useEffect(() => {
    // Limpiar temporizadores previos
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    
    // Salir si no hay wallet o contrato
    if (!address || !contract) {
      setRewards("0");
      setDisplayRewards("0.0000");
      return;
    }
    
    // Cargar datos iniciales
    fetchRewards();
    
    // Iniciar actualización visual
    startVisualCounter();
    
    // Sincronizar con blockchain cada 1 segundos
    intervalRef.current = setInterval(() => {
      if (!blockFetch) {
        fetchRewards();
      }
    }, 1000);
    
    // Limpiar al desmontar
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [address, contract, blockFetch]);
  
  // Mostrar mensaje en modal en lugar de alert
  const showMessage = (message, type = "success") => {
    setModalMessage(message);
    setModalType(type);
    setShowModal(true);
  };
  
  // Cerrar modal
  const closeModal = () => {
    setShowModal(false);
  };
  
  // Manejar reclamo de recompensas
  const handleClaimRewards = async () => {
    if (!address || !contract || isClaiming) return;
    
    try {
      // Verificar recompensas disponibles
      const currentRewards = await calculateRewards();
      if (ethers.BigNumber.from(currentRewards).lte(0)) {
        showMessage(t('stakingBox4.noRewards'), "error");
        return;
      }
      
      setIsClaiming(true);
      setBlockFetch(true);
      
      // Detener contadores y guardamos valor para mensaje
      if (timerRef.current) clearInterval(timerRef.current);
      const rewardsAmount = formatEther(currentRewards);
      
      // Establecer a cero visualmente
      setRewards("0");
      setDisplayRewards("0.0000");
      baseRewardRef.current = "0";
      
      // Llamar al contrato
      const tx = await contract.claimRewards();
      await tx.wait();
      
      // Usar modal en lugar de alert
      showMessage(t('stakingBox4.claimedSuccess', { amount: rewardsAmount }));
      
      // Mantener bloqueado por 5 segundos
      setTimeout(() => {
        setBlockFetch(false);
        
        // Reiniciar todo el proceso
        lastSyncTimeRef.current = Date.now();
        fetchRewards();
        startVisualCounter();
      }, 5000);
      
    } catch (error) {
      console.error("Error al reclamar:", error);
      
      // Simplificación de mensajes de error para mostrar solo mensajes amigables
      let errorMessage = t('stakingBox4.errorClaiming');
      
      // Detectar todos los posibles casos de rechazo de transacción
      if (error.code === 4001 || 
          (error.message && (
            error.message.includes("user rejected") || 
            error.message.includes("User denied") || 
            error.message.includes("user denied") || 
            error.message.includes("rejected") ||
            error.message.includes("cancelled") ||
            error.message.includes("canceled")
          ))) {
        errorMessage = t('stakingBox4.userRejected');
      } else if (error.message && error.message.includes("insufficient funds")) {
        errorMessage = t('stakingBox4.insufficientFunds');
      } else if (error.message && error.message.includes("execution reverted")) {
        errorMessage = t('stakingBox4.contractRejection');
      }
      
      showMessage(errorMessage, "error");
      
      // Desbloquear y reiniciar
      setBlockFetch(false);
      fetchRewards();
      startVisualCounter();
      
    } finally {
      setIsClaiming(false);
    }
  };
  
  return (
    <div className="staking-box">
      {/* Modal personalizado */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className={`modal-container ${modalType}`} onClick={(e) => e.stopPropagation()}>
            <span className="modal-close" onClick={closeModal}>&times;</span>
            <div className="modal-content">
              <h3>{modalType === "success" ? t('stakingBox4.success') : t('stakingBox4.info')}</h3>
              <p className={modalType === "error" ? "smaller-text" : ""}>{modalMessage}</p>
              <button className="modal-button" onClick={closeModal}>
                {t('stakingBox4.accept')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="box-content">
        <div>
          <h4>{t('stakingBox4.totalRewards')}</h4>
          <p className="value">
            {displayRewards} <span className="unit">$eDOP</span>
          </p>
        </div>
      </div>
      
      <button 
        id="claim-rewards" 
        onClick={handleClaimRewards}
        disabled={isClaiming || ethers.BigNumber.from(rewards || "0").lte(0) || blockFetch}
      >
        {isClaiming ? t('stakingBox4.processing') : 
         blockFetch && !isClaiming ? t('stakingBox4.claimed') : 
         t('stakingBox4.claimRewards')}
      </button>
    </div>
  );
};

export default StakingBox4;
